import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';

// Tiempo antes de mostrar la advertencia (25 minutos)
const WARNING_TIME = 25 * 60 * 1000;
// Tiempo total de inactividad (30 minutos)
const LOGOUT_TIME = 30 * 60 * 1000;
// Tiempo de cuenta regresiva de la advertencia (5 minutos)
const COUNTDOWN_TIME = LOGOUT_TIME - WARNING_TIME;

const SessionTimeoutModal = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(Math.floor(COUNTDOWN_TIME / 1000));
  const { currentUser, signOut, resetInactivityTimer } = useAuth();
  
  const warningTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);

  const resetTimers = () => {
    // Limpiar todos los temporizadores
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    
    // Reiniciar el sistema
    setShowWarning(false);
    setCountdown(Math.floor(COUNTDOWN_TIME / 1000));
    
    // Solo iniciar temporizadores si hay un usuario
    if (currentUser) {
      // Programar la advertencia
      warningTimerRef.current = setTimeout(() => {
        setShowWarning(true);
        
        // Iniciar la cuenta regresiva
        countdownTimerRef.current = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(countdownTimerRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        
        // Programar el cierre de sesión después de la cuenta regresiva
        logoutTimerRef.current = setTimeout(() => {
          if (currentUser) {
            signOut();
          }
        }, COUNTDOWN_TIME);
        
      }, WARNING_TIME);
    }
  };
  
  // Resetear los temporizadores cuando cambia el usuario
  useEffect(() => {
    resetTimers();
    
    // Cleanup al desmontar
    return () => {
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    };
  }, [currentUser]);
  
  // Configurar los eventos de actividad
  useEffect(() => {
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart'
    ];
    
    const handleUserActivity = () => {
      if (showWarning) {
        // Si la advertencia está visible, solo resetear al hacer clic en "Continuar"
        return;
      }
      
      // Reiniciar temporizadores de inactividad
      resetTimers();
      // Reiniciar temporizador en AuthContext
      resetInactivityTimer();
    };
    
    activityEvents.forEach(event => {
      document.addEventListener(event, handleUserActivity);
    });
    
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleUserActivity);
      });
    };
  }, [showWarning, resetInactivityTimer]);
  
  const handleContinue = () => {
    resetTimers();
    resetInactivityTimer();
  };
  
  const handleLogout = () => {
    signOut();
  };
  
  // Si no hay usuario o no se muestra la advertencia, no mostrar nada
  if (!currentUser || !showWarning) {
    return null;
  }
  
  // Formatear tiempo restante
  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;
  const timeDisplay = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full">
        <h2 className="text-xl font-bold mb-4">Sesión a punto de expirar</h2>
        <p className="mb-6">
          Su sesión expirará en {timeDisplay} debido a inactividad. 
          ¿Desea continuar con la sesión?
        </p>
        <div className="flex justify-end space-x-4">
          <button 
            onClick={handleLogout}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 rounded-md"
          >
            Cerrar Sesión
          </button>
          <button 
            onClick={handleContinue}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md"
          >
            Continuar Sesión
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutModal;